import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Snackbar,
  Alert,
  Container,
  Tooltip
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { es } from 'date-fns/locale';
import axiosInstance from '../config/axios';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useLocation, Link } from 'react-router-dom';

const Perfil = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    email: '',
    imagenUrl: '',
    rut: '',
    telefonos: {
      fijo: '',
      movil: ''
    },
    genero: '',
    fechaNacimiento: null,
    grupoSanguineo: '',
    alergias: '',
    condicionesMedicas: '',
    medicamentosActuales: '',
    previsionSalud: {
      tipo: '',
      institucion: ''
    },
    contactoEmergencia: {
      nombre: '',
      relacion: '',
      telefono: ''
    }
  });

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(location.state?.showPasswordDialog || false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [usuario, setUsuario] = useState(null);

  useEffect(() => {
    const cargarDatosUsuario = async () => {
      try {
        setLoading(true);
        const usuarioStorage = JSON.parse(localStorage.getItem('usuario') || '{}');
        console.log('🔄 [Perfil] Usuario en localStorage:', usuarioStorage);

        if (!usuarioStorage._id) {
          console.log('❌ [Perfil] No hay ID de usuario');
          setMessage({ 
            type: 'error', 
            text: 'Por favor, inicia sesión nuevamente' 
          });
          return;
        }

        console.log('🔍 [Perfil] Intentando cargar datos del usuario:', usuarioStorage._id);
        const response = await axiosInstance.get(`/usuarios/${usuarioStorage._id}`);
        
        console.log('✅ [Perfil] Datos recibidos:', response.data);
        
        if (!response.data) {
          throw new Error('No se recibieron datos del usuario');
        }

        const data = response.data;
        setFormData(prevState => ({
          ...prevState,
          nombre: data.nombre || '',
          apellidos: data.apellidos || '',
          email: data.email || '',
          imagenUrl: data.imagenUrl || '',
          rut: data.rut || '',
          telefonos: {
            fijo: data.telefonos?.fijo || '',
            movil: data.telefonos?.movil || ''
          },
          genero: data.genero || '',
          fechaNacimiento: data.fechaNacimiento ? new Date(data.fechaNacimiento) : null,
          grupoSanguineo: data.grupoSanguineo || '',
          alergias: data.alergias || '',
          condicionesMedicas: data.condicionesMedicas || '',
          medicamentosActuales: data.medicamentosActuales || '',
          previsionSalud: {
            tipo: data.previsionSalud?.tipo || '',
            institucion: data.previsionSalud?.institucion || ''
          },
          contactoEmergencia: data.contactoEmergencia || {
            nombre: '',
            relacion: '',
            telefono: ''
          }
        }));

        setUsuario(data);
        console.log('👤 [Perfil] Usuario actualizado:', {
          rol: data.rol,
          solicitudCambioRol: data.solicitudCambioRol
        });

      } catch (error) {
        console.error('❌ [Perfil] Error al cargar datos:', error);
        let errorMessage = 'Error al cargar los datos del usuario';
        
        if (error.code === 'ERR_NETWORK') {
          errorMessage = 'Error de conexión. Por favor, verifica tu conexión a internet.';
        } else if (error.response?.status === 401) {
          errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente.';
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        }
        
        setMessage({ 
          type: 'error', 
          text: errorMessage
        });
      } finally {
        setLoading(false);
      }
    };

    cargarDatosUsuario();
  }, []);

  useEffect(() => {
    console.log('🔄 [Perfil] Estado de location:', location.state);
    console.log('🔐 [Perfil] requirePasswordChange en localStorage:', localStorage.getItem('requirePasswordChange'));
    
    if (location.state?.showPasswordDialog) {
      console.log('✨ [Perfil] Mostrando diálogo de cambio de contraseña');
      setOpenPasswordDialog(true);
      setSnackbar({
        open: true,
        message: location.state.message || 'Por favor cambie su contraseña',
        severity: 'warning'
      });
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const usuarioStorage = JSON.parse(localStorage.getItem('usuario') || '{}');
      
      console.log('📝 [Perfil] Intentando actualizar perfil:', usuarioStorage._id);
      console.log('📝 [Perfil] Datos a enviar:', formData);
      
      const response = await axiosInstance.put(`/usuarios/${usuarioStorage._id}/actualizar`, formData);
      
      console.log('✅ [Perfil] Respuesta del servidor:', response.data);
      
      if (response.data) {
        setMessage({
          type: 'success',
          text: 'Perfil actualizado correctamente'
        });
        
        // Actualizar datos en localStorage
        const usuarioActualizado = {
          ...usuarioStorage,
          ...response.data
        };
        localStorage.setItem('usuario', JSON.stringify(usuarioActualizado));
      }
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      let errorMessage = 'Error al actualizar el perfil. Por favor, intente nuevamente.';
      
      if (error.response?.status === 404) {
        errorMessage = 'No se encontró el usuario. Por favor, inicie sesión nuevamente.';
      } else if (error.response?.status === 403) {
        errorMessage = 'No tiene permisos para actualizar este perfil.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      setMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setMessage({
        type: 'error',
        text: 'La imagen no debe superar los 5MB'
      });
      return;
    }

    const formData = new FormData();
    formData.append('imagen', file);

    try {
      setUploading(true);
      const usuarioStorage = JSON.parse(localStorage.getItem('usuario') || '{}');
      const response = await axiosInstance.post(
        `/usuarios/${usuarioStorage._id}/imagen`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.imagenUrl) {
        setFormData(prev => ({
          ...prev,
          imagenUrl: response.data.imagenUrl
        }));

        usuarioStorage.imagenUrl = response.data.imagenUrl;
        localStorage.setItem('usuario', JSON.stringify(usuarioStorage));

        window.dispatchEvent(new Event('storage'));

        setMessage({
          type: 'success',
          text: 'Imagen actualizada correctamente'
        });
      }
    } catch (error) {
      console.error('Error al subir la imagen:', error);
      setMessage({
        type: 'error',
        text: 'Error al subir la imagen. Por favor, intente nuevamente.'
      });
    } finally {
      setUploading(false);
    }
  };

  const handlePasswordChange = async (passwordData) => {
    try {
      console.log('🔄 [Perfil] Iniciando cambio de contraseña');
      await axiosInstance.put('/usuarios/cambiar-password', passwordData);
      console.log('✅ [Perfil] Contraseña actualizada exitosamente');
      setOpenPasswordDialog(false);
      localStorage.removeItem('requirePasswordChange');
      setSnackbar({
        open: true,
        message: 'Contraseña actualizada exitosamente',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ [Perfil] Error al cambiar la contraseña:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Error al cambiar la contraseña',
        severity: 'error'
      });
    }
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const renderSolicitudRolMedico = () => {
    if (!usuario) return null;

    // Si el usuario no es paciente, no mostrar el botón
    if (usuario.rol !== 'paciente') return null;

    const solicitud = usuario.solicitudCambioRol;

    // Si hay una solicitud de tipo médico pendiente
    if (solicitud?.tipo === 'medico' && solicitud.estado === 'pendiente') {
      return (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Ya tienes una solicitud de rol médico pendiente de revisión.
          </Alert>
        </Box>
      );
    }

    // Si la solicitud fue rechazada
    if (solicitud?.tipo === 'medico' && solicitud.estado === 'rechazada') {
      return (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Tu última solicitud fue rechazada. Puedes intentar nuevamente.
          </Alert>
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to="/solicitar-rol-medico"
          >
            SOLICITAR ROL DE MÉDICO
          </Button>
        </Box>
      );
    }

    // Si no hay solicitud, tipo es null, o fue aprobada
    return (
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          ¿Eres médico? Puedes solicitar acceso a funciones especiales.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to="/solicitar-rol-medico"
        >
          SOLICITAR ROL DE MÉDICO
        </Button>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom>
          Mi Perfil
        </Typography>

        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Información Personal
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre"
                name="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Apellidos"
                name="apellidos"
                value={formData.apellidos}
                onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="RUT"
                name="rut"
                value={formData.rut}
                onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono Móvil"
                name="telefonos.movil"
                value={formData.telefonos.movil}
                onChange={(e) => setFormData({
                  ...formData,
                  telefonos: {
                    ...formData.telefonos,
                    movil: e.target.value
                  }
                })}
                helperText="Ingrese su número de teléfono móvil"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Género"
                name="genero"
                value={formData.genero}
                onChange={(e) => setFormData({ ...formData, genero: e.target.value })}
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                <option value="masculino">Masculino</option>
                <option value="femenino">Femenino</option>
                <option value="otro">Otro</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <DatePicker
                  label="Fecha de Nacimiento"
                  value={formData.fechaNacimiento}
                  onChange={(newValue) => setFormData({ ...formData, fechaNacimiento: newValue })}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Grupo Sanguíneo"
                name="grupoSanguineo"
                value={formData.grupoSanguineo}
                onChange={(e) => setFormData({ ...formData, grupoSanguineo: e.target.value })}
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </TextField>
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Información Médica
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Alergias"
                name="alergias"
                multiline
                rows={2}
                value={formData.alergias}
                onChange={(e) => setFormData({ ...formData, alergias: e.target.value })}
                helperText="Separe las alergias con comas"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Condiciones Médicas"
                name="condicionesMedicas"
                multiline
                rows={2}
                value={formData.condicionesMedicas}
                onChange={(e) => setFormData({ ...formData, condicionesMedicas: e.target.value })}
                helperText="Separe las condiciones con comas"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Medicamentos Actuales"
                name="medicamentosActuales"
                multiline
                rows={2}
                value={formData.medicamentosActuales}
                onChange={(e) => setFormData({ ...formData, medicamentosActuales: e.target.value })}
                helperText="Separe los medicamentos con comas"
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Previsión de Salud
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Tipo de Previsión"
                name="previsionSalud.tipo"
                value={formData.previsionSalud.tipo}
                onChange={(e) => setFormData({
                  ...formData,
                  previsionSalud: { ...formData.previsionSalud, tipo: e.target.value }
                })}
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                <option value="FONASA">FONASA</option>
                <option value="ISAPRE">ISAPRE</option>
                <option value="PARTICULAR">PARTICULAR</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Institución"
                name="previsionSalud.institucion"
                value={formData.previsionSalud.institucion}
                onChange={(e) => setFormData({
                  ...formData,
                  previsionSalud: { ...formData.previsionSalud, institucion: e.target.value }
                })}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Contacto de Emergencia
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre"
                name="contactoEmergencia.nombre"
                value={formData.contactoEmergencia.nombre}
                onChange={(e) => setFormData({
                  ...formData,
                  contactoEmergencia: { ...formData.contactoEmergencia, nombre: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Relación"
                name="contactoEmergencia.relacion"
                value={formData.contactoEmergencia.relacion}
                onChange={(e) => setFormData({
                  ...formData,
                  contactoEmergencia: { ...formData.contactoEmergencia, relacion: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono"
                name="contactoEmergencia.telefono"
                value={formData.contactoEmergencia.telefono}
                onChange={(e) => setFormData({
                  ...formData,
                  contactoEmergencia: { ...formData.contactoEmergencia, telefono: e.target.value }
                })}
              />
            </Grid>
          </Grid>

          {/* Sección de Solicitud de Rol Médico */}
          {renderSolicitudRolMedico()}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={loading}
            >
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => setOpenPasswordDialog(true)}
            >
              Cambiar Contraseña
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Diálogo de Cambio de Contraseña */}
      <Dialog open={openPasswordDialog} onClose={handleClosePasswordDialog}>
        <DialogTitle>Cambiar Contraseña</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <TextField
            margin="dense"
            label="Contraseña Actual"
            type={showCurrentPassword ? 'text' : 'password'}
            fullWidth
            value={passwordData.currentPassword}
            onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            margin="dense"
            label="Nueva Contraseña"
            type={showNewPassword ? 'text' : 'password'}
            fullWidth
            value={passwordData.newPassword}
            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
            helperText="Mínimo 8 caracteres"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            margin="dense"
            label="Confirmar Nueva Contraseña"
            type={showConfirmPassword ? 'text' : 'password'}
            fullWidth
            value={passwordData.confirmPassword}
            onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>Cancelar</Button>
          <Button 
            onClick={() => handlePasswordChange(passwordData)} 
            variant="contained"
            disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Perfil; 